/**
 * Debug logging utility
 * Provides conditional logging that only outputs in development mode
 * Reduces console noise in production while preserving useful debug info during development
 */

const isDevelopment = (import.meta.env.DEV || import.meta.env.MODE === 'development') && import.meta.env.MODE !== 'test';

/**
 * Debug logger that only logs in development mode
 */
export const debug = {
  /**
   * Log general debug information
   * @param {...any} args - Arguments to log
   */
  log: (...args) => {
    if (isDevelopment) {
      console.log('[DEBUG]', ...args);
    }
  },

  /**
   * Log informational messages
   * @param {...any} args - Arguments to log
   */
  info: (...args) => {
    if (isDevelopment) {
      console.info('[INFO]', ...args);
    }
  },

  /**
   * Log warning messages (also visible in production for important warnings)
   * @param {...any} args - Arguments to log
   */
  warn: (...args) => {
    if (isDevelopment) {
      console.warn('[WARN]', ...args);
    }
  },

  /**
   * Log error messages (always visible - errors are important)
   * @param {...any} args - Arguments to log
   */
  error: (...args) => {
    console.error('[ERROR]', ...args);
  },

  /**
   * Log with a specific component/module prefix
   * @param {string} prefix - The component or module name
   * @returns {object} Logger with prefixed methods
   */
  withPrefix: (prefix) => ({
    log: (...args) => {
      if (isDevelopment) {
        console.log(`[${prefix}]`, ...args);
      }
    },
    info: (...args) => {
      if (isDevelopment) {
        console.info(`[${prefix}]`, ...args);
      }
    },
    warn: (...args) => {
      if (isDevelopment) {
        console.warn(`[${prefix}]`, ...args);
      }
    },
    error: (...args) => {
      console.error(`[${prefix}]`, ...args);
    },
  }),

  /**
   * Group related logs together
   * @param {string} label - Group label
   * @param {function} fn - Function containing logs to group
   */
  group: (label, fn) => {
    if (isDevelopment) {
      console.group(label);
      fn();
      console.groupEnd();
    }
  },

  /**
   * Log a table (useful for arrays and objects)
   * @param {any} data - Data to display as table
   */
  table: (data) => {
    if (isDevelopment) {
      console.table(data);
    }
  },

  /**
   * Measure execution time
   * @param {string} label - Timer label
   */
  time: (label) => {
    if (isDevelopment) {
      console.time(label);
    }
  },

  /**
   * End time measurement
   * @param {string} label - Timer label
   */
  timeEnd: (label) => {
    if (isDevelopment) {
      console.timeEnd(label);
    }
  },
};

export default debug;
